import { Alert } from '@ghs/components';
import CloseIcon from '@mui/icons-material/Close';
import { Collapse, IconButton, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import type { FeedbackData, FeedbackPanelModel } from './model';

type Props = {
  model: FeedbackPanelModel;
  testId?: string;
};

export default function FeedbackPanel(props: Props) {
  const { model, testId } = props;
  const feedbackData = useUnit(model.$feedbackData);
  // lastVisibleFeedbackData is used to avoid a blinking when closing an error feedback panel. It keeps the last
  // visible feedback data until the panel is closed, so that the content doesn't disappear immediately when the
  // feedbackData is set to null.
  const [lastVisibleFeedbackData, setLastVisibleFeedbackData] = useState<FeedbackData | null>(null);
  const hasContent = Boolean(feedbackData?.title || feedbackData?.message);
  const renderedFeedbackData = feedbackData || lastVisibleFeedbackData;

  useEffect(() => {
    if (hasContent && feedbackData) {
      setLastVisibleFeedbackData(feedbackData);
    }
  }, [feedbackData, hasContent]);

  if (!renderedFeedbackData) {
    return null;
  }

  return (
    <Collapse in={Boolean(feedbackData) && hasContent}>
      <Alert
        data-testid={testId}
        severity={renderedFeedbackData.severity}
        action={
          <IconButton onClick={() => model.setFeedbackData(null)}>
            <CloseIcon />
          </IconButton>
        }
      >
        {renderedFeedbackData.title ? <Typography variant="h6">{renderedFeedbackData.title}</Typography> : null}
        {renderedFeedbackData.message ? <Typography>{renderedFeedbackData.message}</Typography> : null}
      </Alert>
    </Collapse>
  );
}
